/**
 * Frame evaluation: the state at time t as a pure function of the resolution (scene + actions) and t.
 * Later segments override earlier ones per element property; a finished segment holds its last value.
 */
import type { AnimatableProperty, KeyframeValue, Stagger, Track } from '@prism-graphics/scene-schema';
import { ease } from './easing.ts';
import { mixColor, mixOklab } from './color.ts';
import type { Resolution, Segment } from './resolve.ts';

export type PropValue = number | string;
export type PropMap = Partial<Record<AnimatableProperty, PropValue>>;
export type SplitBy = Stagger['by'];

/** The element's designed value of a property (for `{ rel }` keyframes). */
export type BaseValue = (element: string, property: AnimatableProperty) => PropValue | undefined;

/** How many parts (characters, words or lines) a text element has under the data currently shown. */
export type PartCount = (element: string, by: SplitBy) => number;

export interface FrameState {
  visible: boolean;
  version: number;
  values: Map<string, PropMap>;
  /** Staggered values per text element, split, and part index. */
  parts: Map<string, Partial<Record<SplitBy, PropMap[]>>>;
  /** Running crossfades: element → the data version fading out and the progress p of the new one (0..1). */
  fades: Map<string, { from: number; p: number }>;
}

export function visibleAt(res: Resolution, t: number): boolean {
  let v = false;
  for (const f of res.visibility) {
    if (f.t > t) break;
    v = f.visible;
  }
  return v;
}

/** When the graphic last became visible at or before t (undefined while it is not visible): video time zero. */
export function visibleSince(res: Resolution, t: number): number | undefined {
  let v = false;
  let since: number | undefined;
  for (const f of res.visibility) {
    if (f.t > t) break;
    if (f.visible && !v) since = f.t;
    v = f.visible;
  }
  return v ? since : undefined;
}

export function versionAt(res: Resolution, t: number): number {
  let v = res.initialVersion;
  for (const d of res.dataSwitches) {
    if (d.t > t) break;
    v = d.version;
  }
  return v;
}

/** Local time of a segment at t (only meaningful for t >= seg.start). */
export function localTime(seg: Segment, t: number): number {
  if (t >= seg.end) return seg.duration;
  const dt = t - seg.start;
  if (seg.loop) return seg.duration > 0 ? dt % seg.duration : 0;
  return Math.min(Math.max(dt, 0), seg.duration);
}

function resolveValue(v: KeyframeValue, base: PropValue | undefined): PropValue {
  if (typeof v === 'number' || typeof v === 'string') return v;
  // `{ rel }` on a colour means the current (designed or bound) colour.
  if (typeof base === 'string') return base;
  return (typeof base === 'number' ? base : 0) + v.rel;
}

export function sampleTrack(track: Track, local: number, base: PropValue | undefined): PropValue {
  const kfs = track.keyframes;
  const first = kfs[0]!;
  if (local <= first.time || kfs.length === 1) return resolveValue(first.value, base);
  for (let i = 0; i < kfs.length - 1; i++) {
    const a = kfs[i]!;
    const b = kfs[i + 1]!;
    if (local < b.time) {
      const span = b.time - a.time;
      const p = span > 0 ? ease(a.easing, (local - a.time) / span) : 1;
      const va = resolveValue(a.value, base);
      const vb = resolveValue(b.value, base);
      if (typeof va === 'number' && typeof vb === 'number') return va + (vb - va) * p;
      return (track.colorSpace === 'oklab' ? mixOklab : mixColor)(String(va), String(vb), p);
    }
  }
  return resolveValue(kfs[kfs.length - 1]!.value, base);
}

/** Start offset of part i of n under a stagger (ms after the track's own timing). */
export function staggerOffset(s: Stagger, i: number, n: number): number {
  const each = s.within !== undefined && n > 1 ? Math.min(s.each, s.within / (n - 1)) : s.each;
  const k = s.from === 'end' ? n - 1 - i : s.from === 'center' ? Math.abs(i - (n - 1) / 2) : i;
  return k * each;
}

/** Rest values of a part (a character, word or line has no designed transform of its own). */
const PART_BASE: Partial<Record<AnimatableProperty, number>> = { opacity: 1, scaleX: 1, scaleY: 1 };

export function evaluate(res: Resolution, t: number, base: BaseValue, count?: PartCount): FrameState {
  const values = new Map<string, PropMap>();
  const parts: FrameState['parts'] = new Map();
  const fades: FrameState['fades'] = new Map();
  for (const seg of res.segments) {
    if (seg.start > t) break;
    if (seg.until !== undefined && t >= seg.until) continue;
    const local = localTime(seg, t);
    const done = t >= seg.end;
    if (seg.fade) {
      if (!done) {
        const p = seg.duration > 0 ? ease(seg.fade.easing, local / seg.duration) : 1;
        for (const el of seg.fade.elements) fades.set(el, { from: seg.fade.from, p });
      }
      continue;
    }
    for (const track of seg.tracks) {
      const s = track.stagger;
      if (s) {
        const n = count ? count(track.element, s.by) : 0;
        let byEl = parts.get(track.element);
        if (!byEl) parts.set(track.element, (byEl = {}));
        const list = (byEl[s.by] ??= []);
        const b = track.property === 'color' ? base(track.element, 'color') : (PART_BASE[track.property] ?? 0);
        for (let i = 0; i < n; i++) {
          const m = (list[i] ??= {});
          m[track.property] = sampleTrack(track, done ? Infinity : local - staggerOffset(s, i, n), b);
        }
        continue;
      }
      let m = values.get(track.element);
      if (!m) values.set(track.element, (m = {}));
      m[track.property] = sampleTrack(track, local, base(track.element, track.property));
    }
  }
  return { visible: visibleAt(res, t), version: versionAt(res, t), values, parts, fades };
}
